import {
  enumLotStatus,
  enumUnit,
  InventoryLotSortOption,
  LotStatus,
} from '@rematter/frontend-lib-api-client';

import {
  InventoryLotRecord,
  InventoryLotSummaryStats,
} from '../types/inventoryLot';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const inventoryLotsQueryAtom = createQueryAtomFactory(
  'inventory.inventoryLots',
  (params: {
    lotNumber?: string;
    facilityId?: string;
    status?: LotStatus;
    itemId?: string;
    limit?: number;
    offset?: number;
    sorting?: InventoryLotSortOption[];
  }) => ({
    inventoryLots: {
      __args: {
        filter: {
          lotNumber: params.lotNumber,
          facilityId: params.facilityId,
          status: params.status ? [params.status] : undefined,
          itemId: params.itemId,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 25,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        lotNumber: true,
        facilityId: true,
        facility: {
          id: true,
          facilityName: true,
        },
        zoneId: true,
        zone: {
          id: true,
          name: true,
          zoneCode: true,
        },
        status: true,
        description: true,
        quantitySummary: {
          itemId: true,
          item: {
            id: true,
            name: true,
          },
          quantity: {
            value: true,
            unit: true,
          },
          inQuantity: {
            value: true,
            unit: true,
          },
          outQuantity: {
            value: true,
            unit: true,
          },
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      lots: InventoryLotRecord[];
      stats: InventoryLotSummaryStats;
      totalCount: number;
    } => {
      if (!data || !data.inventoryLots || !data.inventoryLots.entries) {
        return {
          lots: [],
          stats: {
            totalLots: 0,
            activeLots: 0,
            closedLots: 0,
          },
          totalCount: 0,
        };
      }

      const lots = data.inventoryLots.entries.map((lot) => ({
        id: lot.id,
        lotNumber: lot.lotNumber,
        facilityId: lot.facilityId,
        facilityName: lot.facility?.facilityName || '',
        zoneId:
          lot.zoneId && typeof lot.zoneId === 'string' ? lot.zoneId : undefined,
        zoneName:
          lot.zone && typeof lot.zone === 'object' && 'name' in lot.zone
            ? (lot.zone.name as string)
            : undefined,
        zoneCode:
          lot.zone && typeof lot.zone === 'object' && 'zoneCode' in lot.zone
            ? (lot.zone.zoneCode as string)
            : undefined,
        status: lot.status,
        description: lot.description || undefined,
        quantitySummary: (lot.quantitySummary || []).map((summary) => ({
          itemId: summary.itemId,
          itemName: summary.item?.name || '',
          quantity: Number(summary.quantity?.value) || 0,
          quantityUnit: summary.quantity?.unit || enumUnit.Pounds,
          inQuantity: Number(summary.inQuantity?.value) || 0,
          outQuantity: Number(summary.outQuantity?.value) || 0,
        })),
        totalItemCount: lot.quantitySummary?.length || 0,
        createdAt: lot.timestamps?.createdAt || '',
        updatedAt: lot.timestamps?.updatedAt || '',
      }));

      const activeLots = lots.filter(
        (lot) => lot.status === enumLotStatus.Active,
      ).length;
      const closedLots = lots.filter(
        (lot) => lot.status === enumLotStatus.Closed,
      ).length;

      return {
        lots,
        stats: {
          totalLots: data.inventoryLots.page?.total || 0,
          activeLots,
          closedLots,
        },
        totalCount: data.inventoryLots.page?.total || 0,
      };
    },
  },
);
